/**
 * Bounded literal description → account rules for bank CSV conversion.
 *
 * A rule assigns the other leg of a row whose *description* matches a
 * user-entered literal — for exports whose category column is missing or
 * useless (every row would otherwise fall back to one account). Only two
 * literal operators exist, `equals` and `contains`: there is deliberately no
 * regex engine, so a pattern can never execute — `A.C` matches the three
 * characters `A.C`, never `AXC`.
 *
 * Matching is deterministic across viewers' locales: both sides are trimmed
 * and folded with `String.prototype.toLowerCase`, which — unlike
 * `toLocaleLowerCase` — does not consult the viewer's locale. Unicode case
 * folding still applies (`CAFÉ` matches `café`); locale-specific foldings
 * do not (a rule for `i` matches `I`, never the Turkish dotless `ı`).
 *
 * Pure and dependency-free apart from the sibling account validator: no DOM,
 * no storage, no network. The converter (`src/lib/csv-to-beancount.ts`)
 * validates these rules into blocking configuration errors and resolves
 * them; the rule editor and profile persistence reuse this seam so all
 * three agree on what a valid rule is.
 */

import { isValidAccount } from "@/lib/beancount-syntax";

/** Literal match operator: exact description, or substring. Never regex. */
export type CsvDescriptionRuleOperator = "equals" | "contains";

/**
 * One ordered description rule. Position in the options array is the
 * priority: the first enabled matching rule wins.
 */
export interface CsvDescriptionRule {
  /** Stable identity, surfaced as `matchedRuleId` on the prepared row. */
  id: string;
  /** `false` skips the rule entirely — matching and validation alike. */
  enabled: boolean;
  operator: CsvDescriptionRuleOperator;
  /** User-entered literal; trimmed before matching and validation. */
  pattern: string;
  /** Destination account for the row's other leg. */
  account: string;
}

/**
 * A rule that passed validation: trimmed, enabled, with a non-empty
 * bounded pattern, a known operator and a valid account.
 */
export type ValidatedCsvDescriptionRule = Pick<
  CsvDescriptionRule,
  "id" | "operator" | "pattern" | "account"
>;

/** Most rules one conversion may carry. Mirrors the profile category bound. */
export const MAX_CSV_DESCRIPTION_RULES = 50;
/** Longest single pattern, in characters. Mirrors the category value bound. */
export const MAX_CSV_DESCRIPTION_RULE_PATTERN_LENGTH = 200;
/** Longest stable rule id, in characters. */
export const MAX_CSV_DESCRIPTION_RULE_ID_LENGTH = 128;

/** Trimmed, locale-independent fold for one side of a comparison. */
export function normalizeDescriptionText(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Whether one validated rule matches a raw description cell. Pure literal
 * comparison — no regex is constructed or executed anywhere in this module.
 */
export function descriptionRuleMatches(
  rule: ValidatedCsvDescriptionRule,
  description: string,
): boolean {
  const text = normalizeDescriptionText(description);
  const pattern = normalizeDescriptionText(rule.pattern);
  if (rule.operator === "equals") return text === pattern;
  return text.includes(pattern);
}

/**
 * First matching rule in order, or `null`. The input is expected to hold
 * only enabled validated rules (see {@link validateCsvDescriptionRules}),
 * so the first match is the first *enabled* match by construction.
 */
export function findMatchingDescriptionRule(
  rules: readonly ValidatedCsvDescriptionRule[],
  description: string,
): ValidatedCsvDescriptionRule | null {
  for (const rule of rules) {
    if (descriptionRuleMatches(rule, description)) return rule;
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Validate unknown input (typed options or a stored profile draft) into the
 * enabled, match-ready rules plus blocking error reasons. Reasons follow the
 * converter's existing `invalid account: <value>` vocabulary so the emitter
 * can report them unchanged as line-0 errors.
 *
 * Disabled rules are inert: they are skipped before any other check, so
 * disabling a broken rule unblocks conversion. Callers must filter blank
 * editor drafts themselves (as the widget already does for category
 * assignments) — an enabled rule with an empty pattern blocks rather than
 * silently matching every row (`contains ""` would match everything).
 */
export function validateCsvDescriptionRules(
  raw: readonly unknown[] | undefined,
): { rules: ValidatedCsvDescriptionRule[]; errors: string[] } {
  if (raw === undefined) return { rules: [], errors: [] };
  if (!Array.isArray(raw)) return { rules: [], errors: ["invalid description rules"] };
  const rules: ValidatedCsvDescriptionRule[] = [];
  const errors: string[] = [];
  if (raw.length > MAX_CSV_DESCRIPTION_RULES) {
    errors.push(
      `too many description rules (max ${MAX_CSV_DESCRIPTION_RULES})`,
    );
  }
  const seenIds = new Set<string>();
  for (const entry of raw) {
    if (!isRecord(entry)) {
      errors.push("invalid description rule");
      continue;
    }
    const id = typeof entry.id === "string" ? entry.id.trim() : "";
    if (id === "" || id.length > MAX_CSV_DESCRIPTION_RULE_ID_LENGTH) {
      errors.push("invalid description rule id");
      continue;
    }
    const enabled = entry.enabled === undefined ? true : entry.enabled;
    if (typeof enabled !== "boolean") {
      errors.push(`invalid description rule flag: ${id}`);
      continue;
    }
    if (!enabled) continue;
    if (entry.operator !== "equals" && entry.operator !== "contains") {
      errors.push(`invalid description rule operator: ${id}`);
      continue;
    }
    const pattern =
      typeof entry.pattern === "string" ? entry.pattern.trim() : "";
    if (
      pattern === "" ||
      pattern.length > MAX_CSV_DESCRIPTION_RULE_PATTERN_LENGTH
    ) {
      errors.push(`invalid description rule pattern: ${id}`);
      continue;
    }
    const account =
      typeof entry.account === "string" ? entry.account.trim() : "";
    if (!isValidAccount(account)) {
      errors.push(`invalid account: ${account}`);
      continue;
    }
    if (seenIds.has(id)) {
      errors.push(`duplicate description rule id: ${id}`);
      continue;
    }
    seenIds.add(id);
    rules.push({ id, operator: entry.operator, pattern, account });
  }
  return { rules, errors };
}
